import cx from 'classnames';
import { pageTitleClass } from '@app/constants/styles';
import { Label } from '@ui/components/ui/label';
import { Textarea } from '@ui/components/ui/textarea';
import { cn } from '@ui/lib/utils';
import { productsReportingPath } from '@app/constants/url';
import { capitalize } from '@app/utils';
import { useOrganizationId } from '@app/utils/hooks';
import {
  ApproveSprintFormValues,
  DisputeSprintFormValues,
  IError,
  ListResponse,
  MembershipEntity,
  ProductEntity,
  SendSprintFormValues,
  SprintEntity,
  SprintProductEntity,
  SprintProductParamsType,
  SprintProductStatus,
  useApproveSprintProduct,
  useDismissDispute,
  useDisputeSprintProduct,
  useMarkApprovalCommentRead,
} from '@data';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutateFunction } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useBackNavigation } from '@app/components/core/useBackNavigation';
import { SprintActionButtons, type SprintActionSpec } from '@app/components/core/sprint-action-buttons';
import {
  ArchiveRestore,
  ChevronLeft,
  CircleCheck,
  MessageSquare,
  MessageSquareOff,
  Package,
  Send,
  ShoppingCart,
} from 'lucide-react';
import {
  ApprovalPopup,
  ApprovalSummaryButton,
  DISPUTED_STATUS,
  DisputePopup,
  SprintAction,
  hasUnreadComment,
  reportStatusesOf,
  sprintActionsFor,
} from '@app/components/core';
import SendSprintPopup from '../sendSprint/SendSprintPopup';
import CloseProductConfirm from '../CloseProductConfirm';
import ConfirmDialog from '../../ConfirmDialog';

export interface ProductHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  product?: ProductEntity;
  sprintProduct?: SprintProductEntity;
  sprint: SprintEntity;
  createSprintProduct: UseMutateFunction<SprintProductEntity, IError, Partial<SprintProductEntity>, unknown>;
  updateSprintProduct: UseMutateFunction<SprintProductEntity, IError, Partial<SprintProductEntity>, unknown>;
  sendSprint: UseMutateFunction<SprintProductEntity, IError, SendSprintFormValues, unknown>;
  sprintProductClients?: ListResponse<MembershipEntity>;
}

const ProductHeader = ({
  product,
  sprintProduct,
  sprint,
  createSprintProduct,
  updateSprintProduct,
  sendSprint,
  sprintProductClients,
}: ProductHeaderProps): ReactElement => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId() as string;
  const { sprintId, productId } = useParams() as Pick<SprintProductParamsType, 'sprintId' | 'productId'>;
  const [isSendModalOpen, setSendModalOpen] = useState<boolean>(false);
  const [isClosing, setClosing] = useState<boolean>(false);
  const [isApproveOpen, setApproveOpen] = useState<boolean>(false);
  const [isDisputeOpen, setDisputeOpen] = useState<boolean>(false);
  const { mutate: approveSprintProduct, isPending: isApproving } = useApproveSprintProduct();
  const { mutate: disputeSprintProduct, isPending: isDisputing } = useDisputeSprintProduct();
  const { mutate: dismissDispute, isPending: isDismissing } = useDismissDispute();
  const [isDismissOpen, setDismissOpen] = useState<boolean>(false);
  // Cleared as the dialog opens rather than as it closes: cancelling and reopening should start blank,
  // and clearing on close would wipe the text before the mutation above has read it.
  const [dismissNote, setDismissNote] = useState<string>('');

  // Approving from this side goes through the client's dialog and the same approve endpoint, so a report
  // approved by us carries a comment and forward list exactly like one the client approved. A bare status
  // update would leave an approval with nobody and nothing recorded against it.
  const handleApprove = (values: ApproveSprintFormValues): void =>
    approveSprintProduct(values, { onSuccess: () => setApproveOpen(false) });

  // Same reasoning for a dispute: a client who sends their remark by mail instead still has it recorded
  // against the report, by the same route their own Dispute button takes.
  const handleDispute = (values: DisputeSprintFormValues): void =>
    disputeSprintProduct(values, { onSuccess: () => setDisputeOpen(false) });

  // The client's comment has to be readable on this side too, so an approved report carries the same
  // "Approved" button they have, opening the same approval.
  const hasApproval =
    sprintProduct?.status === SprintProductStatus.approved || sprintProduct?.status === SprintProductStatus.invoiced;

  const { mutate: markCommentRead } = useMarkApprovalCommentRead();
  const isCommentUnread = hasUnreadComment(sprintProduct);

  // Opening this page is reading the remark: it is on the page, beside the summary it is about, with the
  // pill above it saying so. There is nothing left to open, so nothing left to count as opening it.
  //
  // Once per report rather than once per render — the receipt lands, the report comes back read, and a
  // second call would only be a second write of the same fact.
  const readReport = useRef<string>();
  useEffect(() => {
    if (!sprintProduct || !isCommentUnread || readReport.current === sprintProduct.id) return;
    readReport.current = sprintProduct.id;
    markCommentRead(sprintProduct.id);
  }, [sprintProduct, isCommentUnread, markCommentRead]);

  const handleStatusChange = (status: SprintProductStatus) => {
    if (sprintProduct) {
      updateSprintProduct({ id: sprintProduct?.id, status });
    } else {
      createSprintProduct({ status: SprintProductStatus.closed, sprintId, productId });
    }
  };

  const openDismiss = (): void => {
    setDismissNote('');
    setDismissOpen(true);
  };

  // Which actions this report offers is `sprintActionsFor`'s call, shared with the two lists that offer
  // them too. This map only says what each one looks like here: a button, or on a phone a menu entry.
  // Outline marks the actions open to the reader that are not the step the report is waiting for.
  const actionFor: Record<SprintAction, SprintActionSpec> = {
    // Closing goes through the same missing-reports gate as the products list: closing locks the sprint.
    [SprintAction.close]: { label: t('actions.close'), Icon: Package, onClick: () => setClosing(true) },
    // Whether anything changed decides whether the client hears about it; the server answers that.
    [SprintAction.closeAnswer]: {
      label: sprintProduct?.hasChangesSincePublication ? t('actions.closeWithChanges') : t('actions.close'),
      Icon: Package,
      onClick: () => setClosing(true),
    },
    [SprintAction.reopen]: {
      label: t('actions.reopen'),
      Icon: ArchiveRestore,
      outline: true,
      onClick: () => handleStatusChange(SprintProductStatus.open),
    },
    [SprintAction.approve]: { label: t('actions.approve'), Icon: CircleCheck, onClick: () => setApproveOpen(true) },
    [SprintAction.dispute]: {
      label: t('actions.dispute'),
      Icon: MessageSquare,
      outline: true,
      onClick: () => setDisputeOpen(true),
    },
    // "We looked, and the report stands": the quieter of the two answers to a remark.
    [SprintAction.dismissDispute]: {
      label: t('actions.dismissDispute'),
      Icon: MessageSquareOff,
      outline: true,
      loading: isDismissing,
      onClick: openDismiss,
    },
    [SprintAction.send]: {
      label: `${t('actions.send')} (${sprintProductClients?.count || 0})`,
      Icon: Send,
      onClick: () => setSendModalOpen(true),
    },
    [SprintAction.invoice]: {
      label: t('actions.invoice'),
      Icon: ShoppingCart,
      onClick: () => handleStatusChange(SprintProductStatus.invoiced),
    },
  };
  const actions = sprintActionsFor(sprintProduct);

  // Named for wherever you came from, when that was somewhere other than Products reporting: this sprint is
  // reachable from Insights as well, and a bare "Back" pointing at the list sends a manager who arrived from
  // Insights somewhere they were not.
  //
  // A real link rather than the prototype's bare button: middle-click and "open in new tab" keep working.
  // A plain click walks history back instead of following the href, so this report does not stay on the
  // stack for the page behind it to return to — pushing it there is what made product → sprint → back
  // → back bounce between the two forever. The chevron and its styling are the prototype's.
  const {
    path: backPath,
    label: backLabel,
    goBack,
  } = useBackNavigation(productsReportingPath(organizationId), t('actions.backToProducts'));

  const backOnPlainClick = (e: React.MouseEvent): void => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    goBack();
  };

  const backBtn = (
    <Link
      to={backPath}
      onClick={backOnPlainClick}
      className="mb-3.5 flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" />
      {backLabel}
    </Link>
  );

  return (
    <div>
      <div>
        {backBtn}

        <div className="mb-6 flex flex-wrap items-center justify-between gap-6">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1 className={cx('m-0', pageTitleClass)}>
              {capitalize(product?.name)}{' '}
              <span className="text-xl font-normal text-muted-foreground md:text-2xl">
                {t('sprintCount', { count: sprint.number })}
              </span>
            </h1>
            {/* The outlined pill the member reporting header uses. Grey for a step in the flow, which is a
                sequence rather than a judgement — except a dispute, which is the one status that says
                something is wrong and waiting on us, so it gets its own pill in the warning colour beside
                the step the report is on. Alone, neither half is the whole state: a reopened dispute is
                being worked on and still unanswered at the same time. */}
            {reportStatusesOf(sprintProduct).map(status => (
              <span
                key={status}
                className={cn(
                  'rounded border bg-card px-2 py-[3px] text-xs font-normal leading-none',
                  status === DISPUTED_STATUS
                    ? 'border-destructive/70 text-destructive'
                    : 'border-muted-foreground/50 text-muted-foreground',
                )}
              >
                {status === DISPUTED_STATUS ? t('actions.disputed') : t(capitalize(status) || SprintProductStatus.open)}
              </span>
            ))}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-4">
            {/* The client's own button, so reading their approval works the same on both sides. It sits
                left of the status actions: it reports what happened rather than doing something. */}
            {hasApproval && sprintProduct && (
              <ApprovalSummaryButton
                sprintProduct={sprintProduct}
                unread={isCommentUnread}
                onOpen={() => markCommentRead(sprintProduct.id)}
              />
            )}
            <SprintActionButtons actions={actions} specFor={actionFor} />
          </div>
        </div>
      </div>

      {isApproveOpen && sprintProduct && (
        <ApprovalPopup
          sprintProduct={sprintProduct}
          onClose={() => setApproveOpen(false)}
          approveSprintProduct={handleApprove}
          isApproving={isApproving}
          productId={productId}
          sprintId={sprintId}
        />
      )}

      {isDisputeOpen && sprintProduct && (
        <DisputePopup
          sprintProduct={sprintProduct}
          onClose={() => setDisputeOpen(false)}
          disputeSprintProduct={handleDispute}
          isDisputing={isDisputing}
          productId={productId}
          sprintId={sprintId}
        />
      )}

      {isSendModalOpen && (
        <SendSprintPopup
          handleClick={() => setSendModalOpen(false)}
          sendSprint={sendSprint}
          sprintProduct={sprintProduct}
          sprintProductClients={sprintProductClients?.items}
        />
      )}

      {/* Asked first, the way removing a member is: it settles what the client wrote, and the only copy of
          it is the one on this page. */}
      <ConfirmDialog
        open={isDismissOpen}
        onOpenChange={setDismissOpen}
        title={t('dismissDisputeTitle')}
        description={t('dismissDisputeDescription')}
        confirmLabel={t('actions.dismissDispute')}
        onConfirm={() =>
          sprintProduct && dismissDispute({ sprintProductId: sprintProduct.id, sprintId, productId, note: dismissNote })
        }
      >
        {/* Optional, and said so on the label rather than in a hint below it: the button stays one click
            for the dismissal that needs no explaining, and the client gets a reason whenever there is one
            worth giving. Without this the strongest thing we could tell them was "it stands", which leaves
            them where they started. */}
        <div className="space-y-1.5 text-left">
          <Label htmlFor="dismissDisputeNote">{t('dismissDisputeNoteLabel')}</Label>
          <Textarea
            id="dismissDisputeNote"
            value={dismissNote}
            onChange={event => setDismissNote(event.target.value)}
            placeholder={t('dismissDisputeNotePlaceholder')}
            rows={3}
          />
        </div>
      </ConfirmDialog>

      {isClosing && (
        <CloseProductConfirm
          productId={productId}
          productName={capitalize(product?.name) ?? ''}
          sprint={sprint}
          onCancel={() => setClosing(false)}
          onConfirm={() => {
            setClosing(false);
            handleStatusChange(SprintProductStatus.closed);
          }}
        />
      )}
    </div>
  );
};
export default ProductHeader;
